import Turbolinks from 'turbolinks';

(function () {
  // The search results page (Linear ENG-372): search/index.html.erb. The page keeps its whole
  // state in the address so a copied link reproduces it:
  //
  //   /search?q=topps+chrome&sport=3,7&type=hobby,case&season=45&brand=12&stock=in
  //          &retailers=3%2B&sort=price_asc&view=table&raw=1
  //
  //   q          the keyword (blank = browse every box)
  //   sport      sport_types ids            -> sport[]
  //   type       hobby | retail | case      -> family[]
  //   season     league_years ids           -> year[]
  //   brand      brands ids                 -> brand[]
  //   stock=in   in stock at >= 1 retailer  -> in_stock=1
  //   retailers  3+ | 5+ | 10+ (written 3%2B; a hand-typed 3+ reads as "3 " and is accepted)
  //                                         -> min_retailers
  //   sort       BoxSearch::Results::SORTS (relevance, the default, is left out)
  //   view=table the table instead of the grid (client only)
  //   raw=1      "Search for that instead": no typo correction
  //
  // Ids, not slugs: they are what /search/results takes, the facets carry no brand slug, and
  // the page is noindex, so a readable URL buys nothing. Unknown or malformed values are
  // dropped when the URL is read, and the URL is rewritten in this canonical order.
  //
  // History: a filter, sort or view change PUSHES an entry through Turbolinks' own history
  // (pushHistoryWithLocationAndRestorationIdentifier), so Back / Forward is a Turbolinks
  // restore visit: the page is no-cache (layouts/application), so the restore refetches
  // /search?..., Angular recompiles the body and this controller reads the URL again. A plain
  // history.pushState entry would carry no Turbolinks state, and Turbolinks ignores the
  // popstate of an entry without it: the address would change and the page would not.
  //
  // Requests (contract: the SearchController header comment):
  //   GET    /search/results   the page (page 0) and "Show more" (page n, 24 rows a step:
  //                            1 / 2 / 3 / 4 columns all divide it); NEVER submitted=1 — the
  //                            header dropdown already logged the search (Option A)
  //   POST   /search/watch     box_id, X-CSRF-Token   (signed in only)
  //   DELETE /search/watch     box_id, X-CSRF-Token
  //
  // Every string from the response or the URL reaches the DOM through ng-bind only.
  var RESULTS_URL = '/search/results';
  var WATCH_URL = '/search/watch';
  var SEARCH_PATH = '/search';
  var MAX_Q = 200;
  var MAX_IDS = 100;
  var BRAND_LIMIT = 8;
  var SEASON_LIMIT = 6;
  var SKELETON = 8;
  var FAILED_MESSAGE = 'Search failed. Please try again.';
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var FAMILIES = {
    hobby: { label: 'Hobby', sub: 'Hobby, Jumbo, Booster Box' },
    retail: { label: 'Retail', sub: 'Blaster, Mega, Value, ETB' },
    'case': { label: 'Cases', sub: 'Sealed cases' }
  };
  var SORTS = [
    { value: 'relevance', label: 'Best match' },
    { value: 'release_date', label: 'Release date, newest' },
    { value: 'change_7d', label: '7-day change' },
    { value: 'price_desc', label: 'Avg price, high to low' },
    { value: 'price_asc', label: 'Avg price, low to high' },
    { value: 'retailers', label: 'Most retailers' }
  ];
  var SORT_VALUES = SORTS.map(function (s) { return s.value; });
  var RETAILERS = { '3+': 3, '5+': 5, '10+': 10 };
  // url key -> [api key, chip noun]
  var ID_GROUPS = { sport: ['sport', 'Sport'], season: ['year', 'Season'], brand: ['brand', 'Brand'] };

  // Back from a box page restores the pages "Show more" loaded and the scroll position (lead's
  // pre-merge review, item 4). sessionStorage per exact /search URL (this tab only, gone with
  // it), saved as the visitor scrolls, loads more, changes a filter / sort / view, or starts a
  // Turbolinks visit; read only on a Back / Forward arrival (or a reload), so a fresh search
  // always starts at the top.
  var RESTORE_PREFIX = 'ws-sr-restore:';
  var RESTORE_TTL_MS = 30 * 60 * 1000;

  // The URL as the browser serialises it (an apostrophe becomes %27 there, not in
  // encodeURIComponent), so it compares with location.pathname + location.search.
  function normalizeUrl(url) {
    var a = document.createElement('a');
    a.href = url;
    return a.pathname + a.search;
  }

  // A Turbolinks ADVANCE visit (a link, the header dropdown) is a fresh search even when it
  // ends in a full reload (from a box page: other tracked assets), which the browser reports
  // as 'reload'. turbolinks:before-visit fires for advance visits only, never for a Back /
  // Forward restore; the mark is read and cleared once by the page it names.
  var FRESH_KEY = 'ws-sr-fresh';
  document.addEventListener('turbolinks:before-visit', function (e) {
    try {
      if (e && e.data && e.data.url) window.sessionStorage.setItem(FRESH_KEY, normalizeUrl(e.data.url));
    } catch (err) { /* storage unavailable: worst case a fresh search restores */ }
  });

  function takeFreshMark() {
    try {
      var mark = window.sessionStorage.getItem(FRESH_KEY);
      window.sessionStorage.removeItem(FRESH_KEY);
      return mark;
    } catch (e) {
      return null;
    }
  }

  // Turbolinks 5 sends no action in its turbolinks:visit event; its controller keeps the
  // current visit, whose action is 'restore' for Back / Forward between Turbolinks entries.
  // A full page load has no visit: the browser's navigation type says. Back from a box page
  // is a 'reload' (that page was a full load, so Turbolinks answers the popstate with
  // location.reload()); a plain F5 is one too, and keeping the depth and scroll there is what
  // a browser does anyway.
  function arrivedByHistory() {
    var controller = Turbolinks && Turbolinks.controller;
    var visit = controller && controller.currentVisit;
    if (visit && visit.action) return visit.action === 'restore';
    try {
      var nav = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      return !!nav && (nav.type === 'back_forward' || nav.type === 'reload');
    } catch (e) {
      return false;
    }
  }

  function readRestore(url) {
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(RESTORE_PREFIX + url) || 'null');
      if (!saved || typeof saved.pages !== 'number' || Date.now() - (saved.at || 0) > RESTORE_TTL_MS) return null;
      return saved;
    } catch (e) {
      return null;
    }
  }

  function writeRestore(url, pages, y) {
    try {
      window.sessionStorage.setItem(RESTORE_PREFIX + url, JSON.stringify({ pages: pages, y: y, at: Date.now() }));
    } catch (e) { /* private mode / quota: Back simply starts at the top */ }
  }

  function csrfToken() {
    return $('meta[name="csrf-token"]').attr('content');
  }

  function visit(url) {
    if (Turbolinks && Turbolinks.visit) Turbolinks.visit(url);
    else window.location.assign(url);
  }

  // escapeHtml: toastr inserts its message as HTML by default; every message here is text.
  function toast(kind, message) {
    if (window.toastr && message) window.toastr[kind](message, '', { escapeHtml: true });
  }

  function uniq(list) {
    return list.filter(function (v, i) { return list.indexOf(v) === i; });
  }

  function parseIds(value) {
    return uniq(String(value || '').split(',')
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return /^\d{1,10}$/.test(s) && Number(s) > 0; })
      .map(Number)).slice(0, MAX_IDS);
  }

  function readState(search) {
    var p = new URLSearchParams(search || '');
    var sort = p.get('sort');
    var retailers = String(p.get('retailers') || '').trim().replace(/\+?$/, '+');
    return {
      q: String(p.get('q') || '').replace(/\s+/g, ' ').trim().slice(0, MAX_Q),
      sport: parseIds(p.get('sport')),
      type: uniq(String(p.get('type') || '').toLowerCase().split(',')
        .filter(function (t) { return Object.prototype.hasOwnProperty.call(FAMILIES, t); })),
      season: parseIds(p.get('season')),
      brand: parseIds(p.get('brand')),
      stock: p.get('stock') === 'in',
      retailers: RETAILERS[retailers] || null,
      sort: SORT_VALUES.indexOf(sort) !== -1 ? sort : 'relevance',
      view: p.get('view') === 'table' ? 'table' : 'grid',
      raw: p.get('raw') === '1'
    };
  }

  // The canonical query string of a state (fixed key order; commas and "+" kept readable).
  function queryString(state) {
    var parts = [];
    function add(key, value) { parts.push(key + '=' + value); }
    if (state.q) add('q', encodeURIComponent(state.q));
    if (state.sport.length) add('sport', state.sport.join(','));
    if (state.type.length) add('type', state.type.join(','));
    if (state.season.length) add('season', state.season.join(','));
    if (state.brand.length) add('brand', state.brand.join(','));
    if (state.stock) add('stock', 'in');
    if (state.retailers) add('retailers', state.retailers + '%2B');
    if (state.sort !== 'relevance') add('sort', state.sort);
    if (state.view === 'table') add('view', 'table');
    if (state.raw) add('raw', '1');
    return parts.join('&');
  }

  function apiParams(state, page, q) {
    var p = { q: q, page: page, sort: state.sort };
    if (state.sport.length) p['sport[]'] = state.sport;
    if (state.type.length) p['family[]'] = state.type;
    if (state.season.length) p['year[]'] = state.season;
    if (state.brand.length) p['brand[]'] = state.brand;
    if (state.stock) p.in_stock = 1;
    if (state.retailers) p.min_retailers = state.retailers;
    if (state.raw) p.raw = 1;
    else if (page === 0) p.correct = 1;
    return p;
  }

  function words(text) {
    return String(text || '')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  }

  // The name split into runs, with the leading part of every word that starts with a query
  // word marked bold (the dropdown's rule). Rendered as ng-bind spans, never markup.
  function highlight(name, tokens) {
    var parts = [];
    String(name || '').split(/(\s+)/).forEach(function (piece) {
      if (!piece) return;
      var norm = piece.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
      var best = '';
      if (!/^\s+$/.test(piece)) {
        tokens.forEach(function (t) {
          if (norm.indexOf(t) === 0 && t.length > best.length) best = t;
        });
      }
      if (!best) {
        parts.push({ t: piece, b: false });
        return;
      }
      var seen = 0, i = 0;
      while (i < piece.length && seen < best.length) {
        if (/[a-z0-9]/i.test(piece.charAt(i))) seen++;
        i++;
      }
      parts.push({ t: piece.slice(0, i), b: true });
      if (i < piece.length) parts.push({ t: piece.slice(i), b: false });
    });
    return parts;
  }

  function formatDate(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    if (!m) return '';
    return MONTHS[Number(m[2]) - 1] + ' ' + Number(m[3]) + ', ' + m[1];
  }

  // true when the YYYY-MM-DD release date is after the visitor's today. Compared as date
  // strings, so a UTC-midnight parse never flips the label a few hours early or late.
  function isUpcoming(iso) {
    var m = /^(\d{4}-\d{2}-\d{2})/.exec(iso || '');
    if (!m) return false;
    var now = new Date();
    var today = now.getFullYear() + '-' + ('0' + (now.getMonth() + 1)).slice(-2) + '-' + ('0' + now.getDate()).slice(-2);
    return m[1] > today;
  }

  var currency = (window.Intl && Intl.NumberFormat) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }) : null;

  function formatPrice(value) {
    if (value === null || value === undefined || isNaN(Number(value))) return '';
    return currency ? currency.format(Number(value)) : '$' + Number(value).toFixed(2);
  }

  function formatCount(n) {
    return Number(n || 0).toLocaleString('en-US');
  }

  function plural(n, one, many) {
    return formatCount(n) + ' ' + (n === 1 ? one : many);
  }

  // "updated 3 hours ago" from an iso8601 time; '' when unknown.
  function ago(iso) {
    var t = Date.parse(iso || '');
    if (isNaN(t)) return '';
    var minutes = Math.max(0, Math.round((Date.now() - t) / 60000));
    if (minutes < 1) return 'updated just now';
    if (minutes < 60) return 'updated ' + plural(minutes, 'minute', 'minutes') + ' ago';
    var hours = Math.round(minutes / 60);
    if (hours < 24) return 'updated ' + plural(hours, 'hour', 'hours') + ' ago';
    var days = Math.round(hours / 24);
    if (days < 31) return 'updated ' + plural(days, 'day', 'days') + ' ago';
    var months = Math.round(days / 30);
    return 'updated ' + plural(months, 'month', 'months') + ' ago';
  }

  // price_7d: numbers, oldest first -> the polyline of a 120x32 box, built from numbers only.
  function sparkline(series, change) {
    if (!Array.isArray(series)) return null;
    var values = series.map(Number).filter(function (v) { return isFinite(v); });
    if (values.length < 2) return null;
    var min = Math.min.apply(null, values), max = Math.max.apply(null, values);
    var span = max - min, w = 120, h = 32, pad = 3;
    var points = values.map(function (v, i) {
      var x = pad + (w - 2 * pad) * i / (values.length - 1);
      var y = span === 0 ? h / 2 : h - pad - (h - 2 * pad) * (v - min) / span;
      return x.toFixed(1) + ',' + y.toFixed(1);
    }).join(' ');
    var trend = typeof change === 'number' ? change : values[values.length - 1] - values[0];
    return { points: points, cls: trend > 0 ? 'up' : (trend < 0 ? 'down' : 'flat') };
  }

  function decorate(row, ctx) {
    var r = {
      id: row.id,
      href: '/boxes/' + encodeURIComponent(row.slug || ''),
      thumb: row.box_image || ctx.no_image,
      case_label: row.case_quantity > 1 ? plural(row.case_quantity, 'box', 'boxes') : '',
      is_hot: row.is_hot === true,
      is_watching: row.is_watching === true,
      upc_match: !!ctx.upc,
      retailers_in_stock: Number(row.retailers_in_stock) || 0
    };
    r.parts = ctx.upc ? [{ t: row.name || '', b: false }] : highlight(row.name, ctx.tokens);
    r.kicker = [row.sport_type_name, row.box_type_name].filter(Boolean).join(' · ');
    var upcs = Array.isArray(row.upcs) ? row.upcs : [];
    var typed = String(ctx.query || '').replace(/\D/g, '');
    r.upc = (ctx.upc && upcs.filter(function (u) { return typed && String(u).replace(/\D/g, '').indexOf(typed) !== -1; })[0]) || upcs[0] || '';
    if (row.release_date) {
      r.release = (isUpcoming(row.release_date) ? 'Releases ' : 'Released ') + formatDate(row.release_date);
    } else {
      r.release = '';
    }
    // The table has no Type column: the type and a case's box count ride under the name.
    r.kicker_table = [row.sport_type_name, row.box_type_name, r.case_label, row.release_date ? formatDate(row.release_date) : '']
      .filter(Boolean).join(' · ');
    r.price = formatPrice(row.avg_price);
    var change = typeof row.price_7d_change_percent === 'number' ? row.price_7d_change_percent : null;
    if (change === null) {
      r.delta = '';
      r.delta_cls = 'flat';
    } else {
      r.delta = (change > 0 ? '+' : (change < 0 ? '−' : '')) + Math.abs(change).toFixed(1) + '%';
      r.delta_cls = change > 0 ? 'up' : (change < 0 ? 'down' : 'flat');
    }
    r.spark = sparkline(row.price_7d, change);
    var retailers = plural(r.retailers_in_stock, 'retailer', 'retailers');
    if (r.retailers_in_stock === 0) {
      r.low = { kind: 'out' };
    } else if (row.lowest_price_locked) {
      r.low = { kind: 'locked', retailers: retailers };
    } else if (row.lowest_price !== null && row.lowest_price !== undefined) {
      // "From": the cheapest offer is a best-offer-blacklisted vendor's (ENG-349), so the
      // highlighted price is not the lowest.
      var label = (row.lowest_price_is_lowest === false ? 'From ' : '') + formatPrice(row.lowest_price);
      r.low = { kind: 'price', label: label, retailers: retailers };
    } else {
      r.low = { kind: 'out' };
    }
    return r;
  }

  app.controller('SearchResultsCtrl', [
    '$scope', '$element', '$http', '$timeout', '$q',
    function ($scope, $element, $http, $timeout, $q) {
      var root = $element[0];
      var signed_in = root.getAttribute('data-ws-sr-signed-in') === 'true';
      var unlocked = root.getAttribute('data-ws-sr-unlocked') === 'true';
      var upgrade = root.getAttribute('data-ws-sr-upgrade') || '';
      var starter_limit = parseInt(root.getAttribute('data-ws-sr-starter-limit'), 10) || 0;
      var no_image = root.getAttribute('data-ws-sr-no-image') || '';
      var seq = 0, canceller = null;
      var corrected_q = null;     // the query paging must send after a corrected page 0
      var restore_to = null;      // {pages, y} while a Back arrival reloads the pages it left
      var shown_url = null;       // the URL in the address bar: what Back returns to
      var last_y = 0;             // the visitor's own scroll, kept as they scroll
      var scroll_timer = null;
      var upc = false;
      var names = { sport: {}, season: {}, brand: {} };

      $scope.state = readState(window.location.search);
      $scope.sorts = SORTS;
      $scope.skeleton = new Array(SKELETON);
      $scope.ui = { sort: $scope.state.sort, retailers: $scope.state.retailers, brand_q: '', all_brands: false, all_seasons: false };
      $scope.rows = [];
      $scope.facets = {};
      $scope.chips = [];
      $scope.filter_count = 0;
      $scope.status = 'loading';   // loading | ready | error
      $scope.refreshing = false;
      $scope.loading_more = false;
      $scope.more_failed = false;
      $scope.is_more = false;
      $scope.page = 0;
      $scope.meta_line = '';
      $scope.correction = null;
      $scope.suggested_query = null;
      $scope.closest = [];
      $scope.show_upsell = false;
      $scope.rail_open = false;
      $scope.signup = null;
      $scope.url_selected = false;

      function alive() {
        return document.body.contains(root);
      }

      // ---------- URL ----------

      function currentUrl() {
        var qs = queryString($scope.state);
        return SEARCH_PATH + (qs ? '?' + qs : '');
      }

      function writeUrl(replace) {
        var url = currentUrl();
        shown_url = normalizeUrl(url);
        var controller = Turbolinks && Turbolinks.controller;
        if (controller && controller.pushHistoryWithLocationAndRestorationIdentifier) {
          if (replace) {
            controller.replaceHistoryWithLocationAndRestorationIdentifier(url, controller.restorationIdentifier);
          } else {
            var id = Turbolinks.uuid ? Turbolinks.uuid() : controller.restorationIdentifier;
            controller.pushHistoryWithLocationAndRestorationIdentifier(url, id);
          }
        } else if (window.history && window.history.replaceState) {
          window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
        }
        $scope.share_url = window.location.origin + url;
        $scope.url_selected = false;
      }

      // This instance owns the address bar only while it shows the URL this instance wrote.
      function ownsUrl() {
        return !!shown_url && window.location.pathname + window.location.search === shown_url;
      }

      function saveRestore() {
        // Never while a restore is loading: it would overwrite the entry being restored with
        // fewer pages and y 0.
        if (restore_to || !alive() || $scope.status !== 'ready' || !ownsUrl()) return;
        // shown_url, not currentUrl(): a filter / sort handler changes the state BEFORE apply()
        // saves, and Back returns to the address that was showing.
        // last_y, not the live offset: by pagehide a full-reload navigation (a box page loads
        // other tracked assets) may already have moved the scroll.
        writeRestore(shown_url, $scope.page, last_y);
      }

      // Load the pages the visitor had open, one after another, then put the scroll back.
      function continueRestore() {
        if (!restore_to) return;
        if ($scope.page < restore_to.pages && $scope.is_more) {
          load($scope.page + 1);
          return;
        }
        var y = restore_to.y || 0;
        restore_to = null;
        last_y = y;
        scrollBack(y, 3);
      }

      // Turbolinks puts its own scroll back after a restore render, which can land after this
      // one: check, and scroll again (a few tries, ~1 s in all).
      function scrollBack(y, tries) {
        $timeout(function () {
          if (!alive()) return;
          // The first try always scrolls; a later one only when something moved it away.
          if (tries === 3 || Math.abs((window.pageYOffset || 0) - y) > 40) window.scrollTo(0, y);
          if (tries > 1) scrollBack(y, tries - 1);
        }, tries === 3 ? 0 : 350);
      }

      // ---------- filters ----------

      function countFilters() {
        var s = $scope.state;
        return s.sport.length + s.type.length + s.season.length + s.brand.length + (s.stock ? 1 : 0) + (s.retailers ? 1 : 0);
      }

      function buildChips() {
        var s = $scope.state, chips = [];
        Object.keys(ID_GROUPS).forEach(function (key) {
          s[key].forEach(function (id) {
            chips.push({ key: key + ':' + id, group: key, value: id, label: names[key][id] || ID_GROUPS[key][1] });
          });
        });
        s.type.forEach(function (t) {
          chips.push({ key: 'type:' + t, group: 'type', value: t, label: FAMILIES[t].label });
        });
        if (s.stock) chips.push({ key: 'stock', group: 'stock', label: 'In stock' });
        if (s.retailers) chips.push({ key: 'retailers', group: 'retailers', label: s.retailers + '+ retailers' });
        $scope.chips = chips;
        $scope.filter_count = countFilters();
      }

      function apply() {
        saveRestore(); // Back to these filters comes back to this depth and scroll
        restore_to = null;
        writeUrl(false);
        buildChips();
        load(0);
      }

      $scope.isOn = function (group, value) {
        return $scope.state[group].indexOf(value) !== -1;
      };

      $scope.toggle = function (group, value) {
        var list = $scope.state[group], i = list.indexOf(value);
        if (i === -1) list.push(value);
        else list.splice(i, 1);
        apply();
      };

      $scope.toggleStock = function () {
        $scope.state.stock = !$scope.state.stock;
        apply();
      };

      $scope.setRetailers = function (value) {
        $scope.state.retailers = value || null;
        apply();
      };

      $scope.removeChip = function (chip) {
        if (chip.group === 'stock') $scope.state.stock = false;
        else if (chip.group === 'retailers') $scope.state.retailers = null;
        else $scope.state[chip.group] = $scope.state[chip.group].filter(function (v) { return v !== chip.value; });
        $scope.ui.retailers = $scope.state.retailers;
        apply();
      };

      $scope.clearAll = function () {
        var s = $scope.state;
        s.sport = []; s.type = []; s.season = []; s.brand = []; s.stock = false; s.retailers = null;
        $scope.ui.retailers = null;
        apply();
      };

      $scope.setSort = function (value) {
        $scope.state.sort = SORT_VALUES.indexOf(value) !== -1 ? value : 'relevance';
        apply();
      };

      // The view is client-only: a history entry, no request.
      $scope.setView = function (view) {
        if ($scope.state.view === view) return;
        saveRestore();
        $scope.state.view = view;
        writeUrl(false);
      };

      $scope.searchRaw = function () {
        $scope.state.raw = true;
        apply();
      };

      // A different keyword is a new search: a full visit, filters dropped.
      $scope.searchFor = function (q) {
        visit(SEARCH_PATH + '?q=' + encodeURIComponent(String(q || '').slice(0, MAX_Q)));
      };

      // ENG-376 "Try another search" (nothing close): back to the visible header box (the
      // desktop or the phone variant, like the "/" shortcut), its text selected.
      $scope.tryAnother = function () {
        var inputs = document.querySelectorAll('[data-ws-search] .ws-sd-input');
        window.scrollTo(0, 0);
        for (var i = 0; i < inputs.length; i++) {
          if (inputs[i].offsetParent !== null) {
            inputs[i].focus();
            inputs[i].select();
            return;
          }
        }
      };

      $scope.brandOptions = function () {
        var all = $scope.facets.brand || [];
        var needle = words($scope.ui.brand_q).join(' ');
        if (needle) return all.filter(function (o) { return words(o.name).join(' ').indexOf(needle) !== -1; });
        if ($scope.ui.all_brands || all.length <= BRAND_LIMIT) return all;
        var top = all.slice().sort(function (a, b) { return b.count - a.count; }).slice(0, BRAND_LIMIT);
        return all.filter(function (o) { return o.selected || top.indexOf(o) !== -1; });
      };

      // The season facet in its answer order (newest first): the first SEASON_LIMIT plus any
      // selected season, every season once "Show all" was clicked.
      $scope.seasonOptions = function () {
        var all = $scope.facets.year || [];
        if ($scope.ui.all_seasons || all.length <= SEASON_LIMIT) return all;
        return all.filter(function (o, i) { return i < SEASON_LIMIT || $scope.isOn('season', o.id); });
      };

      $scope.seasonHidden = function () {
        return ($scope.facets.year || []).length - $scope.seasonOptions().length;
      };

      $scope.brandHidden = function () {
        if (words($scope.ui.brand_q).length) return 0;
        return ($scope.facets.brand || []).length - $scope.brandOptions().length;
      };

      // Built once per answer, never from a function in the template: ng-options keys every
      // option object, so a fresh array on each digest never settles ($rootScope:infdig).
      function retailerOptions(facets) {
        var opts = [{ value: null, label: 'Any' }];
        (facets.min_retailers || []).forEach(function (o) {
          opts.push({ value: o.id, label: o.id + '+ (' + formatCount(o.count) + ')' });
        });
        return opts;
      }

      // ---------- data ----------

      function metaLine(rs) {
        var line = plural(rs.total_results, 'box', 'boxes');
        var of = Number(rs.total_unfiltered);
        if ($scope.filter_count && isFinite(of) && of > rs.total_results) line += ' (of ' + formatCount(of) + ')';
        var updated = ago(rs.prices_updated_at);
        return updated ? line + ' · ' + updated : line;
      }

      function remember(facets) {
        (facets.sport || []).forEach(function (o) { names.sport[o.id] = o.name; });
        (facets.year || []).forEach(function (o) { names.season[o.id] = o.name; });
        (facets.brand || []).forEach(function (o) { names.brand[o.id] = o.name; });
      }

      function shapeFacets(facets) {
        var out = {
          sport: facets.sport || [],
          family: (facets.family || []).map(function (o) {
            var f = FAMILIES[o.id] || { label: o.name, sub: '' };
            return { id: o.id, label: f.label, sub: f.sub, count: o.count, selected: o.selected };
          }),
          year: facets.year || [],
          brand: facets.brand || [],
          min_retailers: facets.min_retailers || []
        };
        var stock = facets.in_stock;
        if (stock && (stock.count > 0 || stock.selected)) out.in_stock = stock;
        return out;
      }

      function context(rs) {
        var q = rs.is_corrected && rs.corrected_query ? rs.corrected_query : $scope.state.q;
        return { tokens: words(q), upc: rs.is_upc_match === true, query: q, no_image: no_image };
      }

      function abort() {
        seq++;
        if (canceller) {
          canceller.resolve();
          canceller = null;
        }
      }

      function load(page) {
        abort();
        var id = seq;
        if (page === 0) {
          $scope.more_failed = false;
          $scope.loading_more = false;
          if ($scope.rows.length && $scope.status === 'ready') $scope.refreshing = true;
          else $scope.status = 'loading';
        } else {
          $scope.loading_more = true;
          $scope.more_failed = false;
        }
        var q = page > 0 && corrected_q ? corrected_q : $scope.state.q;
        canceller = $q.defer();
        $http.get(RESULTS_URL, {
          params: apiParams($scope.state, page, q),
          timeout: canceller.promise,
          headers: { 'Accept': 'application/json' }
        }).then(function (resp) {
          if (id !== seq || !alive()) return;
          var rs = resp.data;
          if (!rs || rs.succeed !== true) return fail(page, rs && rs.message);
          if (page === 0) firstPage(rs);
          else nextPage(rs, page);
        }, function () {
          if (id !== seq || !alive()) return;
          fail(page);
        });
      }

      function firstPage(rs) {
        var ctx = context(rs);
        upc = ctx.upc;
        corrected_q = rs.is_corrected && rs.corrected_query ? rs.corrected_query : null;
        remember(rs.facets || {});
        $scope.facets = shapeFacets(rs.facets || {});
        $scope.retailer_options = retailerOptions($scope.facets);
        $scope.rows = (rs.data || []).map(function (row) { return decorate(row, ctx); });
        $scope.page = 0;
        $scope.is_more = rs.is_more === true && rs.is_lost_page !== true;
        $scope.correction = corrected_q ? { corrected: corrected_q, typed: $scope.state.q } : null;
        $scope.suggested_query = rs.suggested_query || null;
        // Zero hits for a keyword: the 3 closest boxes the server already looked up.
        $scope.closest = (rs.data || []).length ? [] : (rs.closest_matches || []).map(function (row) {
          return decorate(row, { tokens: words($scope.state.q), upc: false, query: $scope.state.q, no_image: no_image });
        });
        buildChips();
        $scope.meta_line = metaLine(rs);
        $scope.show_upsell = signed_in && !unlocked && upgrade !== '' &&
          $scope.rows.some(function (r) { return r.low.kind === 'locked'; });
        $scope.status = 'ready';
        $scope.refreshing = false;
        continueRestore();
      }

      function nextPage(rs, page) {
        $scope.loading_more = false;
        if (rs.is_lost_page === true) {
          // The engine hiccuped on this page: keep the rows, offer a retry of the same page.
          $scope.more_failed = true;
          restore_to = null;
          return;
        }
        var ctx = context({ is_corrected: !!corrected_q, corrected_query: corrected_q, is_upc_match: upc });
        var seen = {};
        $scope.rows.forEach(function (r) { seen[r.id] = true; });
        (rs.data || []).forEach(function (row) {
          if (!seen[row.id]) $scope.rows.push(decorate(row, ctx));
        });
        $scope.page = page;
        $scope.is_more = rs.is_more === true;
        if (restore_to) continueRestore();
        else saveRestore();
      }

      function fail(page, message) {
        if (page === 0) {
          // The SQL page answers whatever the engine's state (fallback=1: it never redirects back).
          $scope.fallback_url = '/waxtracker/search?fallback=1' +
            ($scope.state.q ? '&q=' + encodeURIComponent($scope.state.q) : '');
          $scope.rows = [];
          // no results to describe on the error screen
          $scope.correction = null;
          $scope.meta_line = '';
          $scope.closest = [];
          $scope.status = 'error';
          $scope.refreshing = false;
          $scope.error_message = typeof message === 'string' && message ? message : FAILED_MESSAGE;
        } else {
          $scope.loading_more = false;
          $scope.more_failed = true;
        }
        restore_to = null;
      }

      $scope.reload = function () { load(0); };

      // Never while page 0 is being refreshed: the next page of the NEW filters would be
      // appended to the OLD rows (and the refresh it aborts would leave the grid dimmed).
      $scope.showMore = function () {
        if ($scope.loading_more || $scope.refreshing || $scope.status !== 'ready') return;
        load($scope.page + 1);
      };

      // ---------- share ----------

      // The URL pill stays hidden (QA ENG-354 UI 1: Copy link alone is enough) and is shown
      // only as the fallback when the clipboard write is unavailable or refused: revealed,
      // then its text selected after the next render, for a manual Ctrl+C. A new URL hides it.
      function selectPill() {
        $scope.url_selected = true;
        toast('info', 'Press Ctrl+C (or ⌘C) to copy the link.');
        $timeout(function () {
          var pill = root.querySelector('.ws-sr-urlpill');
          if (!pill || !window.getSelection) return;
          var range = document.createRange();
          range.selectNodeContents(pill);
          var sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        });
      }

      $scope.copyLink = function () {
        var url = $scope.share_url;
        if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
          navigator.clipboard.writeText(url).then(function () {
            $scope.$evalAsync(function () { toast('success', 'Link copied'); });
          }, function () {
            $scope.$evalAsync(selectPill);
          });
        } else {
          selectPill();
        }
      };

      // ---------- account tiers ----------

      var last_focus = null;

      $scope.openSignup = function () {
        var text = starter_limit > 0
          ? 'Free accounts can watch up to ' + plural(starter_limit, 'box', 'boxes') + ' with WaxWatch alerts.'
          : 'Free accounts can watch boxes with WaxWatch alerts.';
        last_focus = document.activeElement;
        $scope.signup = true;
        $scope.signup_text = text;
        $timeout(function () {
          var btn = root.querySelector('.ws-sr-modal .ws-btn-primary');
          if (btn) btn.focus();
        });
      };

      $scope.closeSignup = function () {
        $scope.signup = null;
        if (last_focus && last_focus.focus) last_focus.focus();
      };

      $scope.goSignup = function () {
        $scope.signup = null;
        var link = root.querySelector('[data-ws-sr-signup]');
        if (link) link.click();
      };

      function openUpgrade() {
        var link = root.querySelector('[data-ws-sr-upgrade]');
        if (link) link.click();
      }

      $scope.lockClick = function () {
        if (!signed_in) $scope.openSignup();
        else if (upgrade === 'modal') openUpgrade();
      };

      $scope.toggleWatch = function (r) {
        if (!signed_in) return $scope.openSignup();
        if (r.watch_busy) return;
        r.watch_busy = true;
        var watching = r.is_watching;
        $http({
          method: watching ? 'DELETE' : 'POST',
          url: WATCH_URL,
          params: { box_id: r.id },
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() || '' }
        }).then(function (resp) {
          var rs = resp.data || {};
          r.watch_busy = false;
          if (rs.succeed) {
            r.is_watching = rs.is_watching === true;
            toast('success', r.is_watching ? 'Added to your watchlist' : 'Removed from your watchlist');
          } else if (rs.limit) {
            toast('warning', rs.message);
            if (rs.can_upgrade && upgrade === 'modal') openUpgrade();
          } else {
            toast('error', rs.message || 'We could not update your watchlist. Please try again.');
          }
        }, function (resp) {
          r.watch_busy = false;
          var rs = (resp && resp.data) || {};
          if (rs.sign_in) $scope.openSignup();
          else toast('error', rs.message || 'We could not update your watchlist. Please try again.');
        });
      };

      root.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && $scope.signup) $scope.$apply($scope.closeSignup);
      });

      // ---------- start ----------

      // The header dropdown is this page's search box: hand it the query shown here (after
      // this digest, so every dropdown on the page has bound its listener).
      function prefillHeader() {
        if (!$scope.state.q || !window.CustomEvent) return;
        var roots = document.querySelectorAll('[data-ws-search]');
        for (var i = 0; i < roots.length; i++) {
          roots[i].dispatchEvent(new CustomEvent('ws-search:prefill', { detail: { q: $scope.state.q } }));
        }
      }

      // A navigation that ends in a full reload (a box page loads other tracked assets) fires
      // turbolinks:load while this body is still on screen but the address is already the
      // next page's, and Angular compiles the body again. That instance must not rewrite the
      // address (it read no /search state: writeUrl would replace the box URL with /search)
      // nor load anything.
      // The paths Rails routes to search#index: /search, /search/, /search.html (writeUrl(true)
      // then tidies the address to /search).
      if (!/^\/search(?:\/+|\.html)?$/.test(window.location.pathname)) return;

      buildChips();
      writeUrl(true);
      var fresh = takeFreshMark();
      if (fresh !== shown_url && arrivedByHistory()) {
        var saved = readRestore(shown_url);
        if (saved && (saved.pages > 0 || saved.y > 0)) restore_to = saved;
      }
      // Remember where the visitor is: as they scroll (debounced), and when a Turbolinks visit
      // starts (turbolinks:before-visit fires while the address is still this page's; the
      // no-cache meta means before-cache never fires, and pagehide after a box link's reload
      // comes too late, so it is only the plain-unload backstop).
      // The app recompiles the body on every turbolinks:load without destroying old scopes, so
      // a listener of a page no longer on screen removes itself.
      function detach() {
        document.removeEventListener('turbolinks:before-visit', onLeave);
        window.removeEventListener('pagehide', onLeave);
        window.removeEventListener('scroll', onScroll);
        if (scroll_timer) clearTimeout(scroll_timer);
      }
      function onLeave() {
        if (!alive()) return detach();
        if (!restore_to && ownsUrl()) last_y = window.pageYOffset || document.documentElement.scrollTop || 0;
        saveRestore();
      }
      function onScroll() {
        if (!alive()) return detach();
        // The restore's own scrolling is not the visitor's; nor is a scroll after the address
        // already moved on (a full-reload navigation re-renders this body first).
        if (restore_to || !ownsUrl()) return;
        last_y = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (scroll_timer) clearTimeout(scroll_timer);
        scroll_timer = setTimeout(saveRestore, 300);
      }
      document.addEventListener('turbolinks:before-visit', onLeave);
      window.addEventListener('pagehide', onLeave);
      window.addEventListener('scroll', onScroll, { passive: true });
      load(0);
      $timeout(prefillHeader);
    }
  ]);
})();
